// ── Interaction Diagrams — "how this product actually works" per stage ───────
// DSL consumed by @features/dashboards/components/ClientSequenceDiagram's
// parseDsl() + <SequenceDiagram>, the same renderer used elsewhere in DealHub
// (e.g. Scope > Blueprint > SowTab). Kept separate from data.ts because these
// scripts are long.
//
// FrankieOne's V1 (legacy compliance/entity API) and V2 (individuals/workflows
// API) have materially different interaction patterns — V1 has no named
// "workflow" concept (a single synchronous entity+verify call driven by an
// entityProfile recipe, plus a webhook-gated legacy checks fetch for Hosted),
// while V2 wraps everything in executable workflows with async polling. Both
// variants are kept so an existing customer's actual platform version renders
// the diagram that matches their real integration.

export const INTERACTION_DIAGRAMS_V2 = {
  idv: `title IDV & Biometrics — Interaction Flow

participant endUser "Applicant" "Completes capture"
participant clientApp "Client App" "Your product UI"
participant frankieOne "FrankieOne" "Platform"
participant vendor "Biometrics Vendor" "Daon / Onfido / Sumsub"

clientApp->frankieOne: Generate Hosted OneSDK URL
  POST /v2/individuals/hostedUrl
  >{"consentObtained":true,"oneSDKFlowId":"idv_daon","redirectUrl":"https://your-app.com/complete"}
  <{"entityId":"BF-abc123...","url":"https://hosted.uat.frankie.one/idv?token=..."}

frankieOne-->clientApp: Return hosted URL + entityId

clientApp->endUser: Embed hosted URL in iframe

endUser->frankieOne: Opens hosted session, grants consent

self frankieOne: Render document capture screen

endUser->frankieOne: Photographs ID document (front + back)

frankieOne->vendor: Extract document data (OCR) + check document authenticity

vendor-->frankieOne: Document data + authenticity result

self frankieOne: Render face scan / liveness screen

endUser->frankieOne: Completes selfie + liveness check

frankieOne->vendor: Liveness detection + face match against document photo

vendor-->frankieOne: Liveness result + similarity score

self frankieOne: Combine document + biometric results into workflow outcome

frankieOne->endUser: Show verification result on hosted page

frankieOne->>clientApp: Webhook — IDV workflow complete
  POST https://your-server.com/webhook/frankieone
  >{"entityId":"BF-abc123...","workflowExecutionId":"bf-wf-xxxx","status":"COMPLETE"}

clientApp->frankieOne: Fetch full result
  GET /v2/individuals/{entityId}

frankieOne-->clientApp: Return biometrics + document verification result`,

  kyc: `title KYC + Cascade — Interaction Flow

participant clientApp "Client App" "Your product"
participant frankieOne "FrankieOne" "Platform"
participant sources "Cascade Sources" "DVS, Experian, Equifax..."

clientApp->frankieOne: Create individual + execute workflow
  POST /v2/individuals/new/serviceprofiles/DEFAULT/workflows/{workflow}/execute
  >{"workflowName":"AUS-Basic2V-TwoPlus","individual":{"name":{"givenName":"JAMES","familyName":"TESTONE"},"dateOfBirth":{"year":"1990","month":"01","day":"01"}}}

self frankieOne: Start verification cascade for this workflow

group Cascade — waterfall, stops at first PASS
  frankieOne->sources: Source 1 — Document Verification Service (DVS)
  sources-->frankieOne: Result
  frankieOne->sources: Source 2 — Experian (if source 1 insufficient)
  sources-->frankieOne: Result
  frankieOne->sources: Source 3 — Equifax (if still insufficient)
  sources-->frankieOne: Result
  frankieOne->sources: Source 4 — Superchoice (final fallback)
  sources-->frankieOne: Result
end

self frankieOne: Apply ruleset — decide PASS / FAIL / REFER

frankieOne-->clientApp: Return workflow result synchronously
  <{"workflowResult":{"workflowExecutionState":"COMPLETED","individualProfileResult":"PASS"}}

self clientApp: Store result, update application status`,

  aml: `title AML — Interaction Flow

participant clientApp "Client App" "Your product"
participant frankieOne "FrankieOne" "Platform"
participant amlVendor "AML Vendor" "ComplyAdvantage / Refinitiv"

note frankieOne: AML runs as part of the same KYC workflow execution — no separate call needed

frankieOne->amlVendor: Screen individual — PEP (Politically Exposed Person) lists

amlVendor-->frankieOne: PEP result

frankieOne->amlVendor: Screen individual — Sanctions lists

amlVendor-->frankieOne: Sanctions result

frankieOne->amlVendor: Screen individual — Adverse media

amlVendor-->frankieOne: Adverse media result

self frankieOne: Score and combine hits into an overall AML risk level

note frankieOne: Any hit routes the case to a manual review queue for compliance sign-off

frankieOne-->clientApp: AML result included in the workflow response

self frankieOne: If ongoing monitoring is enabled, re-screen periodically and raise new alerts as watchlists change`,

  fraud: `title Fraud — Interaction Flow

participant clientApp "Client App" "Your product"
participant frankieOne "FrankieOne" "Platform"
participant fraudVendor "Fraud Vendor" "Sardine"

clientApp->frankieOne: Collect device + contact signal during the onboarding session
  >{"deviceSessionId":"dvc-sess-xxxx","ipAddress":"203.0.113.4","email":"applicant@example.com","phone":"+61400000000"}

note frankieOne: Fraud checks run as part of the same KYC workflow execution

frankieOne->fraudVendor: Device fingerprint risk check

fraudVendor-->frankieOne: Device risk result (bot / emulator / VPN signals)

frankieOne->fraudVendor: IP address risk check

fraudVendor-->frankieOne: IP risk result

frankieOne->fraudVendor: Email address risk check

fraudVendor-->frankieOne: Email risk result

frankieOne->fraudVendor: Phone number risk check

fraudVendor-->frankieOne: Phone risk result

self frankieOne: Combine signals into an overall fraud risk level

frankieOne-->clientApp: Fraud result included in the workflow response`,

  duplicates: `title Duplicates — Interaction Flow

participant clientApp "Client App" "Your product"
participant frankieOne "FrankieOne" "Platform"
participant entityStore "Entity Store" "Previously verified entities"

clientApp->frankieOne: Submit entity for verification (same request as IDV/KYC)
  >{"givenName":"...","familyName":"...","dateOfBirth":"...","emailAddress":"...","phoneNumber":"..."}

note frankieOne: Duplicate detection runs as part of the same KYC workflow execution

frankieOne->entityStore: Compare against previously verified entities using the configured match rules
  >{"rules":["External reference","Document identifiers","Phone number","Email address","Given + Family name","Given + Family name + DOB","Given + Family name + address"]}

entityStore-->frankieOne: Matching entities (if any), with the rule + strength that matched

self frankieOne: Combine matches into a duplicate result

frankieOne-->clientApp: Duplicate result included in the workflow response

clientApp->frankieOne: (If flagged) Override or confirm the duplicate result
  PATCH /individuals/{entityId}/results/duplicate

frankieOne-->clientApp: Updated duplicate status recorded against the entity`,

  kyb: `title KYB — Interaction Flow

participant clientApp "Client App" "Your product"
participant frankieOne "FrankieOne" "Platform"
participant registry "Business Registry" "ASIC / ABR"

clientApp->frankieOne: Look up the business in the registry
  POST /organizations/lookup
  >{"organizationNumber":{"registrationNumber":"052121347","registryCode":"ACN"},"region":{"country":"AU"}}

frankieOne->registry: Search ASIC/ABR for a matching business

registry-->frankieOne: Matched organisation(s) + organizationToken

frankieOne-->clientApp: Return matched organisation(s)

clientApp->frankieOne: Execute KYB workflow for the selected match
  POST /organizations/workflows/{workflow}/execute
  >{"organizationToken":"org-tok-xxxx..."}

self frankieOne: Create the organisation entity and start the KYB workflow

group KYB checks
  frankieOne->registry: ASIC company information + status
  registry-->frankieOne: Result
  frankieOne->registry: Ownership / UBO resolution
  registry-->frankieOne: Result
  frankieOne->registry: AML entity screening (sanctions, adverse media)
  registry-->frankieOne: Result
end

self frankieOne: Apply blocking definitions and resolve UBOs

frankieOne->>clientApp: Webhook — KYB workflow complete
  POST https://your-server.com/webhook/frankieone

clientApp->frankieOne: Fetch full result
  GET /organizations/{entityId}/serviceprofiles/{service}/workflows/{workflow}/executions/{executionId}

frankieOne-->clientApp: Return business verification result + resolved UBOs/officials/shareholders`,

  tm: `title Transaction Monitoring — Interaction Flow (Simulated)

participant clientApp "Client App" "Your product"
participant frankieOne "FrankieOne" "Platform (illustrative)"
participant ops "Compliance Queue" "Ops / MLRO review"

clientApp->>frankieOne: Submit transaction event
  >{"accountId":"acc-xxxx","amount":9850,"direction":"OUT","counterparty":"Silverline Holdings"}

self frankieOne: Evaluate the transaction against configured rules

group Rule evaluation
  self frankieOne: Reporting threshold — single transaction over the limit?
  self frankieOne: Structuring — repeated transfers just under the threshold?
  self frankieOne: Velocity — unusual number/value of transactions in a time window?
end

note frankieOne: A triggered rule raises an alert with a severity and rationale

frankieOne->>ops: Alert routed to the compliance queue for review
  POST https://your-server.com/webhook/frankieone

ops-->frankieOne: Reviewer marks the alert escalated or cleared

self frankieOne: Update case status and audit trail`,
} as const

export const INTERACTION_DIAGRAMS_V1 = {
  idv: `title IDV & Biometrics (V1) — Interaction Flow

participant endUser "Applicant" "Completes capture"
participant clientApp "Client App" "Your product UI"
participant frankieOne "FrankieOne" "V1 Hosted + Compliance API"
participant vendor "Biometrics Vendor" "Daon / Onfido / Sumsub"

clientApp->frankieOne: Generate hosted onboarding URL
  POST /idv/v2/idvalidate/onboarding-url
  >{"consent":true,"flowId":"idv","customerRef":"cust-123"}
  <{"entityId":"BF-abc123...","url":"https://hosted.uat.frankiefinancial.io/idv?token=..."}

frankieOne-->clientApp: Return hosted URL + entityId

clientApp->endUser: Embed hosted URL in iframe

endUser->frankieOne: Opens hosted session, grants consent

self frankieOne: Render document capture screen

endUser->frankieOne: Photographs ID document (front + back)

frankieOne->vendor: Extract document data (OCR) + check document authenticity

vendor-->frankieOne: Document data + authenticity result

self frankieOne: Render face scan / liveness screen

endUser->frankieOne: Completes selfie + liveness check

frankieOne->vendor: Liveness detection + face match against document photo

vendor-->frankieOne: Liveness result + similarity score

self frankieOne: Combine document + biometric results

frankieOne->endUser: Show verification result on hosted page

frankieOne->>clientApp: Webhook — UpdateCheckEntity (functionResult: COMPLETED)
  POST https://your-server.com/webhook/frankieone

note clientApp: Wait for this webhook before fetching results — calling too early risks a race condition on V1

clientApp->frankieOne: Fetch verification result
  GET /entity/{entityId}/checks

frankieOne-->clientApp: Return checks summary + actionRecommended (PASS/FAIL/REFER)`,

  kyc: `title KYC + Cascade (V1) — Interaction Flow

participant clientApp "Client App" "Your product"
participant frankieOne "FrankieOne" "V1 Compliance API"
participant sources "Cascade Sources" "DVS, Experian, Equifax..."

clientApp->frankieOne: Create + verify entity in one synchronous call
  POST /entity/new/verify/profile/simple
  >{"entity":{"entityProfile":"safe_harbour","entityType":"INDIVIDUAL","name":{"givenName":"JAMES","familyName":"TESTONE"},"dateOfBirth":"1990-01-01"}}

self frankieOne: Start verification cascade for this entity profile

group Cascade — waterfall, stops at first PASS
  frankieOne->sources: Source 1 — Document Verification Service (DVS)
  sources-->frankieOne: Result
  frankieOne->sources: Source 2 — Experian (if source 1 insufficient)
  sources-->frankieOne: Result
  frankieOne->sources: Source 3 — Equifax (if still insufficient)
  sources-->frankieOne: Result
  frankieOne->sources: Source 4 — Superchoice (final fallback)
  sources-->frankieOne: Result
end

self frankieOne: Apply entityProfile rules — decide actionRecommended

frankieOne-->clientApp: Return result synchronously — no polling needed
  <{"entityId":"BF-abc123...","entityProfileResult":{"actionRecommended":"PASS"}}

self clientApp: Store result, update application status

note clientApp: V1 has no named "workflow" — the recipe (entityProfile) and cascade order are configured server-side ahead of time`,

  aml: `title AML (V1) — Interaction Flow

participant clientApp "Client App" "Your product"
participant frankieOne "FrankieOne" "V1 Compliance API"
participant amlVendor "AML Vendor" "ComplyAdvantage / Refinitiv"

note frankieOne: AML runs as part of the same entity verify call — no separate call needed

frankieOne->amlVendor: Screen individual — PEP (Politically Exposed Person) lists

amlVendor-->frankieOne: PEP result

frankieOne->amlVendor: Screen individual — Sanctions lists

amlVendor-->frankieOne: Sanctions result

frankieOne->amlVendor: Screen individual — Adverse media

amlVendor-->frankieOne: Adverse media result

self frankieOne: Score and combine hits into the checks summary

note frankieOne: Any hit routes the case to a manual review queue for compliance sign-off

clientApp->frankieOne: Fetch checks summary
  GET /entity/{entityId}/checks

frankieOne-->clientApp: AML result included in the checks summary

self frankieOne: If ongoing monitoring is enabled, re-screen periodically and raise new alerts as watchlists change`,

  fraud: `title Fraud (V1) — Interaction Flow

participant clientApp "Client App" "Your product"
participant frankieOne "FrankieOne" "V1 Compliance API"
participant fraudVendor "Fraud Vendor" "Sardine"

clientApp->frankieOne: Collect device + contact signal during the onboarding session
  >{"deviceSessionId":"dvc-sess-xxxx","ipAddress":"203.0.113.4","email":"applicant@example.com","phone":"+61400000000"}

note frankieOne: Fraud checks run as part of the same entity verify call

frankieOne->fraudVendor: Device fingerprint risk check

fraudVendor-->frankieOne: Device risk result (bot / emulator / VPN signals)

frankieOne->fraudVendor: IP address risk check

fraudVendor-->frankieOne: IP risk result

frankieOne->fraudVendor: Email address risk check

fraudVendor-->frankieOne: Email risk result

frankieOne->fraudVendor: Phone number risk check

fraudVendor-->frankieOne: Phone risk result

self frankieOne: Combine signals into the checks summary

clientApp->frankieOne: Fetch checks summary
  GET /entity/{entityId}/checks

frankieOne-->clientApp: Fraud result included in the checks summary`,

  // Transaction Monitoring is simulated/illustrative regardless of platform
  // version — there's no live TM endpoint on either V1 or V2 today.
  tm: INTERACTION_DIAGRAMS_V2.tm,

  // Duplicate detection works the same way on V1 (route-based) and V2 (workflow-based)
  // customers — same match rules, same underlying comparison against prior entities.
  duplicates: INTERACTION_DIAGRAMS_V2.duplicates,

  // KYB runs on the organizations API, which only exists on V2 — reuse the same
  // diagram for a V1 customer since there's no separate V1 KYB integration.
  kyb: INTERACTION_DIAGRAMS_V2.kyb,
} as const

/** Back-compat default — the "New Customer" demo flow always targets V2. */
export const INTERACTION_DIAGRAMS = INTERACTION_DIAGRAMS_V2

export type InteractionDiagramStage = keyof typeof INTERACTION_DIAGRAMS_V2

/** Pick the right diagram variant for a given (possibly unknown) platform version. Defaults to V2 when unset. */
export function getInteractionDiagram(stage: InteractionDiagramStage, platformVersion: 'v1' | 'v2' | 'unknown' | undefined): string {
  return platformVersion === 'v1' ? INTERACTION_DIAGRAMS_V1[stage] : INTERACTION_DIAGRAMS_V2[stage]
}
